import { useState, useEffect, useCallback, useMemo } from "react";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import { formatFunction, parseFunction } from "./helpers";
import { ICustomer } from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ICustomerSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/**
 * Sepcialized wrapper for "built-in" oc Customer select field.
 * @param props -
 */
export const CustomerSelectField = (props: ICustomerSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  ///////////////////////////////////////
  // MANAGE CUSTOMER LIST
  ///////////////////////////////////////
  const structures = useSelector((state: IRootState) => state.structures);
  const customers = useMemo(() => {
    return structures?.customerStructure?.customers ?? [];
  }, [structures?.customerStructure?.customers]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, customers, valueKey, isMulti),
    [customers, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Flight Flag Selector"}
      isMulti={isMulti}
      options={customers}
      getOptionLabel={(option: ICustomer) => `${option.name}`}
      getOptionValue={(option: ICustomer) => option?._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Customer${isMulti && "s"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
